import { type FormEvent, useState, type ReactElement, useCallback, useEffect } from 'react'
import * as S from './contact.styled'
import logo from '../../assets/images/logo-positvo.png'
import Input from '../input/input'
import { type ContactProps, type ContactFormData } from './contact.props'
import { Button } from '../button/button'
import Checkbox from '../checkbox/checkbox'
import { HubSpotCreateLead } from '../../../data/usecase/hubspot-create-lead'
import { Gt7GenerateLead } from '../../../data/usecase/gt7-create-lead'
import { AxiosHttpClient } from '../../../infra/adapters/axios-http-client'

enum ContactState {
  READY,
  SENDING,
  ERROR
}

const createLead = new HubSpotCreateLead()
const axiosHttpClient = new AxiosHttpClient()
const generateLead = new Gt7GenerateLead(axiosHttpClient)

const mask = (value: string): string => {
  if (!value) return ''
  let v = value
    .replace(/\D/g, '')
    .replace(/^0/, '')

  if (v.length > 11) {
    v = v.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3')
  } else if (v.length > 7) {
    v = v.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3')
  } else if (v.length > 2) {
    v = v.replace(/^(\d{2})(\d{0,5})/, '($1) $2')
  } else if (v.trim() !== '') {
    v = v.replace(/^(\d*)/, '($1')
  }
  return v.substring(0, 15)
}

export function Contact ({ validation }: ContactProps): ReactElement {
  const [state, setState] = useState<ContactState>(ContactState.ERROR)
  const [data, setData] = useState<ContactFormData>({} as unknown as ContactFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = useCallback((evt: FormEvent<HTMLFormElement>): void => {
    evt.preventDefault()

    void (async () => {
      try {
        setState(ContactState.SENDING)
        await createLead.handle({
          email: data.email,
          name: data.name,
          phone: data.phone,
          optInCommunication: data.accept,
          campanhasAdicionais: 'lp_viva_sorte'
        })
        await generateLead.handle({
          lead: {
            email: data.email,
            name: data.name,
            phone: data.phone
          }
        })
        setState(ContactState.READY)
        window.location.href = `https://idpv.cartaodetodos.com.br/dados-pessoais/?from_lead=1&idpv=593fe813-3579-442f-b0c2-17a883e92e24&nome=${data.name}&email=${data.email}&telefone=${data.phone}&accept_notifications=${data.accept}&accept_notifications=${data.accept}`
      } catch (error) {
        console.log(error)
        setState(ContactState.ERROR)
        setTimeout(() => { setState(ContactState.READY) }, 3000)
      }
    })()
  }, [data])

  useEffect(() => {
    setErrors({
      accept: validation.validate('accept', data.accept ? 'true' : 'false'),
      email: validation.validate('email', data.email),
      name: validation.validate('name', data.name),
      phone: validation.validate('phone', data.phone)
    })
  }, [data])

  useEffect(() => {
    if (Object.values(errors).filter(error => !!error).length !== 0) {
      setState(ContactState.ERROR)
      return
    }
    setState(ContactState.READY)
  }, [errors])

  return <S.Container>
    <S.Content>
      <S.Header>
        <figure>
          <img src={logo} alt="" />
        </figure>
        <h1>Ainda está com dúvida? Receba mais informações</h1>
      </S.Header>
      <S.Form onSubmit={handleSubmit}>
        <div>
          <Input
            label="Nome completo*"
            name="name"
            onChange={setData}
            placeholder="ex: Maria Lucia Almeida"
            value={data.name}
          />
          {errors.name && data.name !== undefined && <S.Error>{errors.name}</S.Error>}
        </div>
        <div>
          <Input
            label="E-mail*"
            name="email"
            onChange={setData}
            placeholder="ex: ana@gmail.com"
            value={data.email}
          />
          {errors.email && data.email !== undefined && <S.Error>{errors.email}</S.Error>}
        </div>
        <div>
          <Input
            label="Whatsapp"
            name="phone"
            onChange={setData}
            mask={mask}
            placeholder="ex: (11) 99999-9999"
            value={data.phone}
          />
          {errors.phone && data.phone !== undefined && <S.Error>{errors.phone}</S.Error>}
        </div>
        <Checkbox
          label={<>Autorizo o envio de comunicações do Cartão de TODOS por <strong>canais digitais</strong></>}
          name="accept"
          onChange={setData}
        />
        {state === ContactState.SENDING && <Button type="submit" disabled={true}>{'Enviando os dados...'}</Button>}
        {state === ContactState.ERROR && <Button type="submit" disabled={true}>{'Quero saber mais'}</Button>}
        {state === ContactState.READY && <Button type="submit" disabled={false}>{'Quero saber mais'}</Button>}
      </S.Form>
    </S.Content>
  </S.Container>
}
